import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Logo from './Logo';

const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showUserMenu, setShowUserMenu] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // TODO: Implement search functionality
    console.log('Searching for:', searchQuery);
  };

  // Helper function to get full avatar URL
  const getAvatarUrl = (avatarPath) => {
    if (!avatarPath) return '';
    // If it's already a full URL (e.g., Google OAuth avatar), return as is
    if (avatarPath.startsWith('http://') || avatarPath.startsWith('https://')) {
      return avatarPath;
    }
    // If it's a relative path, prepend API domain
    const API_URL = process.env.REACT_APP_API_URL || 'https://api.getaizest.com/api';
    const baseUrl = API_URL.endsWith('/api') ? API_URL.slice(0, -4) : API_URL;
    return `${baseUrl}${avatarPath}`;
  };

  // Get plan styling based on subscription
  const getPlanStyle = () => {
    const planName = user?.subscription?.plan_name || 'Free';

    switch (planName.toLowerCase()) {
      case 'free':
        return {
          gradient: 'bg-gradient-to-r from-blue-500 to-blue-600',
          icon: '💎',
          name: 'Free Plan'
        };
      case 'basic':
        return {
          gradient: 'bg-gradient-to-r from-yellow-500 to-amber-500',
          icon: '⭐',
          name: 'Basic Plan'
        };
      case 'professional':
        return {
          gradient: 'bg-gradient-to-r from-purple-500 to-purple-600',
          icon: '👑',
          name: 'Professional Plan'
        };
      default:
        return {
          gradient: 'bg-gradient-to-r from-blue-500 to-blue-600',
          icon: '💎',
          name: planName
        };
    }
  };

  const planStyle = getPlanStyle();

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
      <div className="flex items-center justify-between px-6 py-2">
        {/* Logo */}
        <div className="flex items-center">
          <Logo size="sm" />
        </div>

        {/* Search Bar */}
        <div className="flex-1 max-w-2xl mx-6">
          <form onSubmit={handleSearch}>
            <div className="relative">
              <input
                type="text"
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <div className="absolute left-2.5 top-2 text-gray-400">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
          </form>
        </div>

        {/* Right Side Actions */}
        <div className="flex items-center space-x-3">
          {/* Subscription Status */}
          <div className={`flex items-center space-x-1.5 px-2.5 py-1 ${planStyle.gradient} text-white rounded-lg text-xs font-medium`}>
            <span className="text-sm">{planStyle.icon}</span>
            <span>{planStyle.name}</span>
          </div>

          {/* Upgrade Button */}
          <button
            onClick={() => navigate('/subscription')}
            className="px-3 py-1.5 bg-primary-600 text-white rounded-lg text-xs font-medium hover:bg-primary-700 transition"
          >
            Upgrade
          </button>

          {/* User Menu or Login/Register */}
          {user ? (
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center space-x-1.5 px-2 py-1 rounded-lg hover:bg-gray-50 transition"
              >
                {user?.avatar_url ? (
                  <img
                    src={getAvatarUrl(user.avatar_url)}
                    alt={user?.full_name || user?.email}
                    className="w-7 h-7 rounded-full object-cover border border-gray-200"
                    onError={(e) => {
                      console.error('Failed to load avatar in header:', getAvatarUrl(user.avatar_url));
                      e.target.style.display = 'none';
                      // Show default avatar on error
                      e.target.parentElement.innerHTML = `<div class="w-7 h-7 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full flex items-center justify-center text-white text-sm font-semibold">${user?.full_name?.charAt(0).toUpperCase() || 'U'}</div>`;
                    }}
                  />
                ) : (
                  <div className="w-7 h-7 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full flex items-center justify-center text-white text-sm font-semibold">
                    {user?.full_name?.charAt(0).toUpperCase() || 'U'}
                  </div>
                )}
                <svg className="w-3.5 h-3.5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Dropdown Menu */}
              {showUserMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1">
                  <div className="px-4 py-2 border-b border-gray-200">
                    <p className="text-sm font-medium text-gray-900">{user?.full_name || 'User'}</p>
                    <p className="text-xs text-gray-500 truncate">{user?.email || 'user@example.com'}</p>
                  </div>
                  <button
                    onClick={() => {
                      navigate('/profile');
                      setShowUserMenu(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Profile Settings
                  </button>
                  <button
                    onClick={() => {
                      navigate('/subscription');
                      setShowUserMenu(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    Subscription
                  </button>
                  <button
                    onClick={() => {
                      navigate('/billing');
                      setShowUserMenu(false);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center justify-between"
                  >
                    <span>Billing</span>
                    {user?.has_new_invoice && (
                      <span className="ml-2 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full">
                        !
                      </span>
                    )}
                  </button>
                  <div className="border-t border-gray-200"></div>
                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-50"
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              <button
                onClick={() => navigate('/login')}
                className="px-3 py-1.5 text-xs font-medium text-gray-700 hover:text-gray-900 transition"
              >
                Login
              </button>
              <button
                onClick={() => navigate('/register')}
                className="px-3 py-1.5 bg-gray-900 text-white rounded-lg text-xs font-medium hover:bg-gray-800 transition"
              >
                Sign Up
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
